import { useState, useCallback } from 'react';
import olreadiaService from '../services/olreadiaService';

const toMessage = (err: any, fallback: string) =>
  err?.response?.data?.message || err?.message || fallback;

export const useBusinessLocations = (businessEntityId: string | null) => {
  const [locations, setLocations] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState({ totalElements: 0, totalPages: 1 });

  const list = useCallback(async (params = {}) => {
    if (!businessEntityId) return null;
    setLoading(true);
    setError(null);
    try {
      const res = await olreadiaService.businessEntities.locations.list(businessEntityId, params);
      const content = Array.isArray(res?.content) ? res.content : [];
      setLocations(content);
      setPage({
        totalElements: res?.totalElements ?? content.length,
        totalPages: res?.totalPages ?? 1,
      });
      return res;
    } catch (err) {
      const message = toMessage(err, 'Failed to load business locations');
      setError(message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [businessEntityId]);

  const get = useCallback(async (locationId: string) => {
    if (!businessEntityId) return null;
    try {
      return await olreadiaService.businessEntities.locations.get(businessEntityId, locationId);
    } catch (err) {
      throw err;
    }
  }, [businessEntityId]);

  const create = useCallback(async (payload: any, actorId?: string | null) => {
    if (!businessEntityId) throw new Error('Business entity ID required');
    try {
      return await olreadiaService.businessEntities.locations.create(businessEntityId, payload, actorId);
    } catch (err) {
      throw err;
    }
  }, [businessEntityId]);

  const update = useCallback(async (locationId: string, payload: any, actorId?: string | null) => {
    if (!businessEntityId) throw new Error('Business entity ID required');
    try {
      return await olreadiaService.businessEntities.locations.update(businessEntityId, locationId, payload, actorId);
    } catch (err) {
      throw err;
    }
  }, [businessEntityId]);

  const remove = useCallback(async (locationId: string, actorId?: string | null) => {
    if (!businessEntityId) throw new Error('Business entity ID required');
    try {
      await olreadiaService.businessEntities.locations.delete(businessEntityId, locationId, actorId);
    } catch (err) {
      throw err;
    }
  }, [businessEntityId]);

  return { locations, loading, error, page, list, get, create, update, remove };
};

export default useBusinessLocations;
